import { queryOptions } from '@tanstack/react-query';

import { customerSaleQueryOptions } from '@/api/customer/customerSaleQuery';
import type {
  CustomerSaleQueryKey,
  CustomerSaleResponse,
} from '@/api/customer/customerSaleQuery/types';
import type { ApiError } from '@/hooks/mutations/auth';
import { Logger } from '@/utils/logging';

type CustomerSaleResponseWithErrors = CustomerSaleResponse & {
  errors?: unknown[];
};

const RETRY_DELAY = 1500;

export const customerSaleWithAppointmentQueryOptions = (
  saleOrderId: string,
) => {
  const base = customerSaleQueryOptions(saleOrderId);
  const { queryFn: baseQueryFn } = base;

  if (!baseQueryFn) {
    throw new Error('customerSaleQueryOptions must define a queryFn');
  }

  return queryOptions<
    CustomerSaleResponse,
    ApiError,
    CustomerSaleResponse,
    CustomerSaleQueryKey
  >({
    ...base,
    queryFn: async (ctx) => {
      const data = (await baseQueryFn(ctx)) as CustomerSaleResponseWithErrors;

      if (data.errors?.length) {
        Logger.error('Sale has errors', { saleOrderId, errors: data.errors });
        const e = new Error('SALE_HAS_ERRORS');

        const details = (data.errors as { message: string }[]).at(0)?.message;

        if (details) {
          Object.assign(e, { details });
        }

        throw e;
      }

      if (!data.items[0]?.appointment?.id) {
        throw new Error('SALE_NO_APPOINTMENT_ID');
      }

      return data;
    },
    retryDelay: RETRY_DELAY,
    retry: (_failureCount, error) =>
      _failureCount <= 5 &&
      (error.message === 'SALE_NO_APPOINTMENT_ID' ||
        error.message === 'SALE_HAS_ERRORS'),
  });
};
